import { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../i18n/LangContext";
import { api, extractError } from "../api/client";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { APP } from "../constants/testIds";

export default function Profile() {
  const { user, refresh } = useAuth();
  const { t } = useLang();
  const [full_name, setName] = useState("");
  const [whatsapp_number, setWa] = useState("");
  const [view_preference, setView] = useState("AUTO");
  const [ok, setOk] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) {
      setName(user.profile?.full_name || "");
      setWa(user.profile?.whatsapp_number || "");
      setView(user.view_preference || "AUTO");
    }
  }, [user]);

  const save = async (e) => {
    e.preventDefault();
    setError("");
    setOk(false);
    setBusy(true);
    try {
      await api.patch("/profiles/me", { full_name, whatsapp_number: whatsapp_number || null });
      await api.patch("/users/me", { view_preference });
      await refresh();
      setOk(true);
    } catch (err) {
      setError(extractError(err).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <h1 className="font-[Outfit] text-4xl font-extrabold text-white tracking-tight">{t("profile.title")}</h1>
        <p className="text-slate-400 mt-2">{t("profile.subtitle")}</p>
      </div>
      <Card className="bg-[#161923] border-white/5">
        <CardHeader>
          <CardTitle className="text-white font-[Outfit]">{t("dashboard.profile")}</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={save} className="space-y-5">
            <div className="space-y-2">
              <Label className="text-slate-300">{t("common.email")}</Label>
              <Input value={user?.email || ""} disabled className="bg-[#1C202E] border-white/10 text-slate-400 h-11" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">{t("common.fullName")}</Label>
              <Input data-testid={APP.profileFullName} value={full_name} onChange={(e) => setName(e.target.value)} className="bg-[#1C202E] border-white/10 text-white h-11" required />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">{t("common.whatsapp")}</Label>
              <Input data-testid={APP.profileWhatsapp} value={whatsapp_number} onChange={(e) => setWa(e.target.value)} className="bg-[#1C202E] border-white/10 text-white h-11" placeholder="+62..." />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">{t("profile.viewPreference")}</Label>
              <Select value={view_preference} onValueChange={setView}>
                <SelectTrigger className="bg-[#1C202E] border-white/10 text-white h-11"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#161923] border-white/10 text-slate-200">
                  <SelectItem value="AUTO">{t("profile.auto")}</SelectItem>
                  <SelectItem value="MOBILE">{t("profile.mobile")}</SelectItem>
                  <SelectItem value="DESKTOP">{t("profile.desktop")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {error && <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">{error}</div>}
            {ok && <div data-testid={APP.profileSuccess} className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-300">{t("profile.saved")}</div>}
            <div className="flex justify-end">
              <Button data-testid={APP.profileSave} type="submit" disabled={busy} className="bg-[#FF3B30] hover:bg-[#E02E24] text-white font-semibold h-11 px-6">
                {busy ? t("common.loading") : t("common.save")}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
